import { BookCheck } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Modal } from '@/components/ui/modal'
import { Textarea } from '@/components/ui/textarea'
import { CheckRow, Field } from '@/components/common/FormBits'
import { fmtMoney } from '../../../shared/format'
import { usePostLegacyExpense, type CustomerExpense, type LegacyPostInput } from '../../expenses'
import { toastError } from '../../ui'
import { DocumentFields, PaymentMethodPicker } from './ExpensePaymentFields'

/**
 * Eski (önceki kayıt) gideri muhasebeye aktarma: ödeme şekli ve belge/KDV seçilir, onaylanan gider gibi işlenir (nakit/şahsi
 * kartta personele borç, avansta mahsup). Ya da gerekçeyle muhasebe dışı bırakılır (ör. muhasebeci elle işledi).
 */
export function LegacyPostModal({ expense: e, onClose }: { expense: CustomerExpense; onClose: () => void }) {
  const { t } = useTranslation()
  const post = usePostLegacyExpense()
  const [v, setV] = useState<LegacyPostInput>({ exclude: false, note: '', paymentMethod: e.paymentMethod, cardId: '', documentType: 'receipt', vatRate: '0', invoiceNo: '' })
  const set = (p: Partial<LegacyPostInput>) => setV((x) => ({ ...x, ...p }))
  const ok = v.exclude ? !!v.note.trim() : v.paymentMethod !== 'company_card' && (v.documentType !== 'invoice' || !!v.invoiceNo.trim())
  return (
    <Modal
      open
      size="lg"
      onOpenChange={(o) => !o && onClose()}
      icon={<BookCheck />}
      title={t('crm.expenses.legacyPost.title')}
      description={t('crm.expenses.legacyPost.hint', { amount: fmtMoney(e.amount) })}
      footer={
        <>
          <Button variant="cancel" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            variant={v.exclude ? 'destructive' : 'default'}
            disabled={!ok || post.isPending}
            onClick={() =>
              post.mutate(
                { id: e.id, input: v },
                {
                  onSuccess: () => {
                    toast.success(t(v.exclude ? 'crm.expenses.legacyPost.excludedToast' : 'crm.expenses.legacyPost.postedToast'))
                    onClose()
                  },
                  onError: toastError,
                },
              )
            }
          >
            {t(v.exclude ? 'crm.expenses.legacyPost.exclude' : 'crm.expenses.legacyPost.post')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <CheckRow checked={v.exclude} onChange={(x) => set({ exclude: x.target.checked })} hint={t('crm.expenses.legacyPost.excludeHint')}>
          {t('crm.expenses.legacyPost.excludeLabel')}
        </CheckRow>
        {v.exclude ? (
          <Field id="lp-note" label={t('crm.expenses.legacyPost.note')}>
            <Textarea id="lp-note" rows={2} value={v.note} onChange={(x) => set({ note: x.target.value })} />
          </Field>
        ) : (
          <>
            {/* Şirket kartı eski kayıtta seçilemez: kart ekstresi dönemi geçmiş olabilir. */}
            <PaymentMethodPicker value={v.paymentMethod} cardId="" cards={[]} onChange={(p) => set(p)} />
            <DocumentFields documentType={v.documentType} vatRate={v.vatRate} invoiceNo={v.invoiceNo} amount={Number(e.amount)} category={e.category} idPrefix="lp" onChange={set} />
            <p className="text-xs text-muted-foreground">{t('crm.expenses.legacyPost.effect')}</p>
          </>
        )}
      </div>
    </Modal>
  )
}
